import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { Observable, delay, of, throwError } from 'rxjs';
import {
	isRealLoginMode,
	mockScenario,
	mockSetupSignals,
	MockScenario,
} from '../testing/mock-mode';
import { RECORDED_CALLNOTE } from '../testing/mock-callnote.fixture';
import {
	ORDER_ERROR_ADDRESS_INVALID,
	OUTBOX_RESULT_ALREADY_EXISTS,
	OUTBOX_RESULT_ORDERED,
} from '../models/sip-account-order.model';
import {
	LINK_OFFER_STATUS_COMPLETED,
	LINK_OFFER_STATUS_EXPIRED,
	LINK_OFFER_STATUS_INVALID,
	LINK_OFFER_STATUS_OPEN,
	LINK_OFFER_STATUS_ADDRESS_INCOMPLETE,
} from '../models/sip-order-link.model';

/**
 * PS-6495 — TEMPORARY. Stays until the live release, then goes with the rest of the mock harness
 * (see `mock-mode.ts` for the removal list and for why carrying it on test is safe).
 *
 * Answers every endpoint this flow touches so that in mock mode **nothing leaves the browser**.
 * Anything not listed here still goes to the network on purpose: a silent catch-all would make an
 * unmocked call look mocked, which is the one failure mode a harness like this must not have.
 *
 * Register it FIRST, before the bearer interceptor, so a mocked request never even gets a token
 * attached — and so it is obvious in the chain that nothing downstream can reach the network.
 */
const MOCK_LATENCY_MS = 250;

/** The brand's legal URLs as the localization API's `url` tree; the rows of PS-6495-url-inserts.sql. */
const legalUrl = (value: string) => ({ live: { translation: value }, test: { translation: value } });
const MOCK_LEGAL_URLS = {
	egroup_agb: legalUrl('https://www.egroup-technologies.com/agb'),
	egroup_homepage: legalUrl('https://www.egroup-technologies.com/'),
	egroup_service_description: legalUrl('https://www.egroup-technologies.com/leistungsbeschreibung'),
	egroup_privacy: legalUrl('https://www.egroup-technologies.com/datenschutz'),
	user_rights: legalUrl('https://www.gesetze-im-internet.de/tkg_2021/__71.html'),
};

export const mockBackendInterceptor: HttpInterceptorFn = (req, next) => {
	const scenario = mockScenario();
	if (scenario === null) {
		return next(req);
	}

	const path = extractPath(req.url);

	if (path.includes('/document/v1/outboundtasks/callbackoncallnote')) {
		// GET returns the recorded callnote with this scenario's three fields overridden; POST is the
		// task creation, which must succeed without creating anything.
		if (req.method === 'GET') {
			// The candidate list is EMPTY unless a scenario asks for it, because that is the common
			// case: a new customer has nothing to pick from. The override happens here rather than in
			// the fixture — the fixture is a recording of a real response and stays one, which is also
			// what keeps `withCandidates` honest.
			const candidates =
				scenario === 'withCandidates' ? {} : { availableOutgoingNumbers: [] };
			// PS-6526 ships a NUMBER plus an ISO-4217 code, and three of the four readings are
			// counter-intuitive — so each gets a scenario rather than living only in a unit test.
			// The shape mirrors Jana's recorded live response for PS 22833.
			const price = {
				priceMissing: { sipExtensionPrice: null, currency: 'EUR' },
				priceFree: { sipExtensionPrice: 0, currency: 'EUR' },
				priceNoCurrency: { sipExtensionPrice: 4.9, currency: null },
				priceForeign: { sipExtensionPrice: 3.5, currency: 'GBP' },
			}[scenario as string] ?? { sipExtensionPrice: 4.9, currency: 'EUR' };
			return respond({
				...RECORDED_CALLNOTE,
				...mockSetupSignals(scenario),
				...candidates,
				...price,
				// PS-6646: the mocked sign-in's phone service (see `phoneServices`). Set here, not in the
				// fixture, which stays a recording of a response from before the field existed.
				phoneServiceId: MOCK_PHONE_SERVICE_ID,
			});
		}
		return respond({});
	}

	// In real-login mode the whole auth chain has to reach the real server: the proxy is what decides
	// whether the credentials are good, and check_token is what resolves the phone service the
	// booking will target. Faking either would make the rehearsal prove nothing about the login.
	if (isRealLoginMode() && (path.startsWith('/authorization/') || path.startsWith('/rest/login'))) {
		return next(req);
	}

	/*
	 * The brand's legal URLs. Answered here so a mocked walkthrough still shows the consent sentence
	 * WITH its links: the real call needs a token the mock login never obtains, and without this the
	 * review would see unlinked document names and read it as a defect.
	 *
	 * The shape is the localization API's - globalKey, category, localKey, `{ translation }` - and
	 * the values are the rows in PS-6495-url-inserts.sql, so what is reviewed is what will ship.
	 */
	if (path.startsWith('/localization/v2/i18n')) {
		return respond({ url: MOCK_LEGAL_URLS });
	}

	// The login is a plain JSON POST to a same-origin path now, not an OAuth token exchange. Both
	// error shapes are the ones the real proxy sends: an HTTP error carrying `reason`.
	if (path.startsWith('/rest/login')) {
		if (scenario === 'loginRejected') {
			return fail(500, 'Internal Server Error', {
				result: 'error',
				reason: 'generic',
				message: 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später noch einmal.',
			});
		}
		if (scenario === 'loginTwoFactor') {
			return fail(400, 'Bad Request', {
				result: 'error',
				reason: 'twofactor_code_missing',
				message: 'Bitte geben Sie Ihren Bestätigungscode ein.',
			});
		}
		return respond({
			access_token: 'mock-access-token',
			token_type: 'Bearer',
			scope: 'read write',
			expires_in: 43199,
		});
	}

	if (path.includes('/authorization/v2/oauth/check_token')) {
		return respond({ details: { phoneServiceData: phoneServices(scenario) } });
	}

	// Before the `/bookingmgmt/v2/outbox` branch, whose `includes` would swallow the link paths.
	if (path.endsWith('/bookingmgmt/v2/outbox/linkorders/offer')) {
		return respond(linkOffer(scenario));
	}
	if (path.endsWith('/bookingmgmt/v2/outbox/linkorders')) {
		if (scenario === 'linkOrderFails') {
			return fail(502, 'Bad Gateway');
		}
		return respond({
			carrier: 'OUTBOX',
			number: '+493222000111',
			phoneNumberId: 999001,
			phoneServiceId: 22833,
			result: OUTBOX_RESULT_ORDERED,
		});
	}

	if (path.includes('/bookingmgmt/v2/outbox')) {
		if (scenario === 'orderFails') {
			return fail(500, 'Internal Server Error');
		}
		if (scenario === 'orderAddressInvalid') {
			// PS-6605: bookingmgmt's refusal of an unusable contract address. Only `errorCode` is read.
			return fail(422, 'Unprocessable Entity', {
				errorCode: ORDER_ERROR_ADDRESS_INVALID,
				message: 'The contract address could not be used for the order',
			});
		}
		if (scenario === 'orderRejected') {
			// A 200 carrying no number. The real service never produces this — every success path runs
			// through toResponse(), which always fills the number — so this exercises the client's
			// defensive branch rather than a documented backend shape. A real refusal is `orderFails`.
			return respond({ phoneServiceId: phoneServices(scenario)[0].phoneServiceId });
		}
		return respond({
			carrier: 'OUTBOX',
			number: '+493222000111',
			phoneNumberId: 999001,
			phoneServiceId: phoneServices(scenario)[0].phoneServiceId,
			// The real values, copied from bookingmgmt's OutboxOrderResponse. ALREADY_EXISTS is the
			// idempotent hit and just as much a success — an earlier version of this mock returned
			// 'OK', a value I invented, which taught a contract that does not exist.
			result:
				scenario === 'orderAlreadyExists' ? OUTBOX_RESULT_ALREADY_EXISTS : OUTBOX_RESULT_ORDERED,
		});
	}

	if (path.startsWith('/phoneservice/v3/cli-verification/')) {
		return mockCliVerification(scenario, path);
	}

	// Deliberately not mocked. Better a visible real request than a silent fake one.
	return next(req);
};

/** Polls per attempt id, so a scripted call can move IN_FLIGHT -> ANSWERED -> final. */
const pollCounts = new Map<string, number>();

/**
 * PS-6589 - the own-number verification, one scenario per outcome family. A walkthrough without a
 * scenario of its own (e.g. `allMissing`) ends VERIFIED, the happy path.
 */
function mockCliVerification(scenario: MockScenario, path: string): Observable<HttpResponse<unknown>> {
	if (path.endsWith('/number/validate')) {
		if (scenario === 'validateDown') {
			return fail(503, 'Service Unavailable');
		}
		const outcome =
			{
				validateMobile: 'MOBILE',
				validateUnavailable: 'UNAVAILABLE',
				validateAlreadyVerified: 'ALREADY_VERIFIED_FOR_CUSTOMER',
				numberUnverified: 'OK',
				numberMissing: 'OK',
			}[scenario as string] ?? 'NOT_ELIGIBLE_NO_SIP_TRUNK';
		return respond({ outcome, phoneNumber: '+4930123456' });
	}
	if (path.endsWith('/manual-requests')) {
		return respond({ phoneNumber: '+4930123456', receivedAt: berlinLocal(0) });
	}
	if (path.endsWith('/attempts')) {
		const refusals: Record<string, object> = {
			verifyLocked: { outcome: 'LOCKED', retryAfter: berlinLocal(12 * 60) },
			verifyRateLimited: { outcome: 'RATE_LIMITED_NUMBER', retryAfter: berlinLocal(2) },
			verifyInFlight: { outcome: 'ATTEMPT_IN_FLIGHT' },
			verifyCallNotPlaced: { outcome: 'CALL_NOT_PLACED' },
			verifyNoSipYet: { outcome: 'NOT_ELIGIBLE_NO_SIP_TRUNK' },
		};
		const refusal = refusals[scenario as string];
		if (refusal) {
			return respond({ phoneNumber: '+4930123456', ...refusal });
		}
		const attemptId = 4700 + Math.floor(Math.random() * 100);
		pollCounts.set(String(attemptId), 0);
		const clientTimeout = scenario === 'verifyClientTimeout';
		return respond({
			outcome: 'TRIGGERED',
			phoneNumber: '+4930123456',
			attemptId,
			code: '4827',
			status: 'IN_FLIGHT',
			expiresAt: berlinLocal(clientTimeout ? 0.25 : 10),
			deadline: berlinLocal(clientTimeout ? 0.25 : 12),
		});
	}
	const attempt = /\/attempts\/(\d+)(\/cancel)?$/.exec(path);
	if (attempt) {
		const [, attemptId, cancel] = attempt;
		if (cancel) {
			const status = scenario === 'verifyCancelRace' ? 'VERIFIED' : 'CANCELLED';
			return respond(attemptBody(Number(attemptId), status));
		}
		const count = (pollCounts.get(attemptId) ?? 0) + 1;
		pollCounts.set(attemptId, count);
		if (count <= 2 || scenario === 'verifyClientTimeout') {
			return respond(attemptBody(Number(attemptId), 'IN_FLIGHT'));
		}
		if (count <= 4) {
			return respond(attemptBody(Number(attemptId), 'ANSWERED'));
		}
		const final =
			{
				verifyWrongCode: 'WRONG_CODE_EXHAUSTED',
				verifyNotAnswered: 'NOT_ANSWERED',
				verifyBusy: 'BUSY',
				verifyVoicemail: 'VOICEMAIL',
				verifyLockedOut: 'LOCKED_OUT',
				verifyTimedOut: 'TIMED_OUT',
			}[scenario as string] ?? 'VERIFIED';
		return respond({
			...attemptBody(Number(attemptId), final),
			...(final === 'LOCKED_OUT' ? { lockedUntil: berlinLocal(12 * 60) } : {}),
		});
	}
	return fail(404, 'Not Found');
}

function attemptBody(attemptId: number, status: string): object {
	return {
		attemptId,
		status,
		phoneNumber: '+4930123456',
		expiresAt: berlinLocal(10),
		deadline: berlinLocal(12),
		failedEntries: status === 'WRONG_CODE_EXHAUSTED' ? 3 : 0,
		remainingCalls: 4,
		remainingTotal: 2,
	};
}

/** Now plus some minutes as the API writes it: Berlin wall-clock time without an offset. */
function berlinLocal(minutesFromNow: number): string {
	const parts = new Intl.DateTimeFormat('sv-SE', {
		timeZone: 'Europe/Berlin',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hourCycle: 'h23',
	}).format(new Date(Date.now() + minutesFromNow * 60_000));
	return parts.replace(' ', 'T');
}

/** The offer for the `link*` scenarios; every other scenario gets OPEN so a link URL never hits the network. */
function linkOffer(scenario: MockScenario): unknown {
	const translations = RECORDED_CALLNOTE.translations;
	const status = {
		linkCompleted: LINK_OFFER_STATUS_COMPLETED,
		linkExpired: LINK_OFFER_STATUS_EXPIRED,
		linkInvalid: LINK_OFFER_STATUS_INVALID,
		linkNotOrderable: LINK_OFFER_STATUS_ADDRESS_INCOMPLETE,
	}[scenario as string];
	if (status) {
		return { status, translations };
	}
	return {
		status: LINK_OFFER_STATUS_OPEN,
		monthlyPrice: 4.9,
		currency: 'EUR',
		companyName: 'Mock Company GmbH',
		brandId: 1,
		languageId: 1,
		translations,
		legalLinks: { url: MOCK_LEGAL_URLS },
	};
}

/** `ambiguousToken` is the whole point of returning a list here rather than a single object. */
/**
 * `brandId` is carried because the real `check_token` carries it, and the order screen's legal links
 * are looked up by it. Without it a mocked walkthrough shows the consent sentence unlinked, which
 * is the brand-has-no-rows degradation rather than the normal case - and a reviewer would read it
 * as a defect. 1 is ebuero DE, the brand this flow ships for.
 */
function phoneServices(scenario: MockScenario): { phoneServiceId: number; brandId: number }[] {
	if (scenario === 'ambiguousToken') {
		return [
			{ phoneServiceId: MOCK_PHONE_SERVICE_ID, brandId: 1 },
			{ phoneServiceId: 87308, brandId: 1 },
		];
	}
	if (scenario === 'wrongAccount') {
		return [{ phoneServiceId: 99999, brandId: 1 }];
	}
	return [{ phoneServiceId: MOCK_PHONE_SERVICE_ID, brandId: 1 }];
}

/** The phone service of the recorded callnote, and the one a mocked sign-in resolves to. */
const MOCK_PHONE_SERVICE_ID = 22833;

/** A little latency, so the busy states are actually visible while reviewing. */
function respond(body: unknown): Observable<HttpResponse<unknown>> {
	return of(new HttpResponse({ status: 200, body })).pipe(delay(MOCK_LATENCY_MS));
}

/**
 * The optional body matters for the login: the service tells "the proxy said no" from "we never
 * reached the proxy" by whether the error carries a `reason`, so a mocked rejection without one
 * would exercise the wrong branch.
 */
function fail(status: number, statusText: string, error?: unknown): Observable<never> {
	return throwError(() => new HttpErrorResponse({ status, statusText, error })).pipe(
		delay(MOCK_LATENCY_MS),
	) as Observable<never>;
}

function extractPath(url: string): string {
	try {
		return new URL(url, window.location.origin).pathname;
	} catch {
		return '';
	}
}
